import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { ApiError, errorMessages } from '../../api/client'
import {
  activateAccount,
  blockAccount,
  createAccount,
  newAccountFormQuery,
  removeAccount,
  usersAccountsQuery,
  type UsersShell,
} from '../../api/settingsUsers'
import { longDateTime } from '../../easeedesk/format'
import { fromNow, stamp } from '../../inventoree/format'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { AccountWorkspace } from './AccountWorkspace'
import { AccessLevelLegend } from './AccessLevelLegend'
import { offeredAccessLevels } from './accessLevels'
import { esc, failed, tableRequest } from './shared'

/**
 * Lista de conturi - `active/index.php`, `blocked/index.php`,
 * `inactive/index.php` - plus modala "New user" (`active/addUserModal.php`).
 *
 * Cele trei ecrane sunt ACEEASI lista, cu alt `status`: asa era si in portal
 * (`UsersPage.initDatatable($table, cb, 'active' | 'blocked' | 'inactive')`,
 * o singura cerere `usersData`).
 *
 * COLOANELE sunt cele din `ActiveUsersModel` (`public/js/settings/users.js`),
 * in aceeasi ordine, cu aceleasi insigne.
 *
 * BUTOANELE de pe rand se deseneaza dupa STATUS, ca in `Users_summary::getData`:
 * activ -> Remove si Block (fiecare cu dreptul lui), sters sau blocat ->
 * Activate. Drepturile vin de la server, in `shell.may`.
 */

const ORDER_NAMES = [
  '',
  'firstname',
  'username',
  'email',
  'group_name',
  '',
  'it_manager',
  'last_login',
  'last_seen',
  'last_mobile_login',
  'phone',
  'position',
  'plan_name',
  'created',
  '',
  'source',
]

/**
 * De unde vine contul - dedus pe server (`AccountOrigin`): Local, AD / LDAP,
 * SCIM sau SSO. Indicele 15 e nou, dar coloana sta dupa Role: ordinea din
 * lista e cea de afisare, indicele e doar numele ei pentru sortare si ascundere.
 */
const ORIGIN_CLASS: Record<string, string> = {
  local: 'label-default',
  directory: 'label-info',
  scim: 'label-primary',
  sso: 'label-primary',
}

export function originBadge(row: Record<string, unknown>): string {
  const label = String(row.origin_label ?? '')
  if (!label) return ''
  const cls = ORIGIN_CLASS[String(row.origin ?? '')] ?? 'label-default'
  return `<span class="label ${cls} od-account-origin" title="${esc(row.origin_hint)}">${esc(label)}</span>`
}

/** `moment(x).tz(TIME_ZONE).format('LLL')` din `ActiveUsersModel`. */
function longText(value: unknown): string {
  const when = stamp(value)
  return when ? longDateTime(when) : ''
}

function accountColumns(may: Record<string, boolean>): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 30,
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.ID)}" data-type=""><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 1,
      title: 'Name',
      orderable: true,
      visible: true,
      html: (row) =>
        `<i class="fas fa-user ${Number(row.status) === 0 ? 'color-green2' : 'color-danger'}" aria-hidden="true"></i> ` +
        `<strong>${esc(row.firstname)} ${esc(row.lastname)}</strong>`,
    },
    {
      index: 2,
      title: 'Username',
      orderable: true,
      visible: true,
      html: (row) => `<span style="color:#1EC94A ">${esc(row.username)}</span>`,
    },
    { index: 3, title: 'Email', orderable: true, visible: true, html: (row) => esc(row.email) },
    { index: 4, title: 'Group', orderable: true, visible: true, html: (row) => esc(row.group_name) },
    {
      index: 5,
      title: 'Role',
      orderable: true,
      visible: true,
      html: (row) => `<span class="label label-default">${esc(row.role_name)}</span>`,
    },
    {
      index: 15,
      title: 'Source',
      orderable: true,
      visible: true,
      html: originBadge,
    },
    {
      index: 6,
      title: 'IT Manager',
      orderable: true,
      visible: true,
      className: 'center',
      html: (row) =>
        Number(row.it_manager) === 1
          ? '<span class="badge badge-success">Yes</span>'
          : '<span class="badge badge-danger">No</span>',
    },
    {
      index: 7,
      title: 'Last Login',
      orderable: true,
      visible: true,
      html: (row) => esc(longText(row.last_login)),
    },
    {
      index: 8,
      title: 'Last Activity',
      orderable: true,
      visible: true,
      html: (row) => {
        const when = stamp(row.last_seen)
        if (!when) return ''
        /* Sub 300 de minute = "Online", ca in `ActiveUsersModel`. */
        const minutes = Math.round((Date.now() - when.getTime()) / 60000)
        const online = minutes < 300
        return (
          `<p><i class="fas fa-circle ${online ? 'color-success faa-flash animated' : 'color-danger'}"></i> ` +
          `<label class="label ${online ? 'label-success' : 'label-danger'}">${online ? 'Online' : 'Inactive'}</label></p>` +
          esc(longDateTime(when))
        )
      },
    },
    {
      index: 9,
      title: 'Last Mobile Login',
      orderable: true,
      visible: true,
      html: (row) => esc(longText(row.last_mobile_login)),
    },
    { index: 10, title: 'Phone', orderable: true, visible: true, html: (row) => esc(row.phone) },
    { index: 11, title: 'Position', orderable: true, visible: true, html: (row) => esc(row.position) },
    {
      index: 12,
      title: 'Work Hour Plan',
      orderable: true,
      visible: true,
      html: (row) =>
        row.plan_name ? `<i class="far fa-calendar-check" aria-hidden="true"></i> ${esc(row.plan_name)}` : '',
    },
    {
      index: 13,
      title: 'Created',
      orderable: true,
      visible: true,
      html: (row) => {
        const when = stamp(row.created)
        return when ? esc(fromNow(when)) : ''
      },
    },
    {
      index: 14,
      title: 'Actions',
      orderable: false,
      visible: true,
      html: (row) => {
        const id = Number(row.ID)
        if (Number(row.status) === 0) {
          let html = ''
          if (may['settings.users_active_remove.read']) {
            html += `<button class="btn btn-xs btn-danger od-remove-user" data-user="${id}">Remove</button>`
          }
          if (may['settings.users_active_block.read']) {
            html += ` <button class="btn btn-xs btn-warning od-block-user" data-user="${id}">Block</button>`
          }
          return html
        }
        return `<button class="btn btn-xs btn-success od-activate-user" data-user="${id}">Activate</button>`
      },
    },
  ]
}

export function AccountsPanel({ status, shell }: { status: 'active' | 'blocked' | 'inactive'; shell: UsersShell }) {
  const client = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [state, setState] = useTableState(`settings-users-${status}`, {
    start: 0,
    length: 25,
    /* `order: [[1, 'desc']]` din `UsersPage.initDatatable`. */
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => accountColumns(shell.may), [shell.may])
  const table = useQuery({
    ...usersAccountsQuery(status, tableRequest(state, ORDER_NAMES, 'ID')),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]
  const reload = () => void client.invalidateQueries({ queryKey: ['settings', 'users', 'accounts'] })

  const act = (
    kind: 'remove' | 'block' | 'activate',
    id: number,
  ): void => {
    const ask =
      kind === 'remove'
        ? 'Remove this user?'
        : kind === 'block'
          ? 'Block this user?'
          : 'Activate this user?'
    if (!confirm(ask)) return
    const call = kind === 'remove' ? removeAccount : kind === 'block' ? blockAccount : activateAccount
    void call(id)
      .then(() => {
        toast(kind === 'remove' ? 'User removed.' : kind === 'block' ? 'User blocked.' : 'User activated.', 'Success')
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  const heading =
    status === 'active' ? 'Active' : status === 'blocked' ? 'Blocked' : 'Inactive'
  const lead =
    status === 'active'
      ? 'Manage, create or edit active user'
      : status === 'blocked'
        ? 'Manage blocked user'
        : 'Manage inactive user'

  return (
    <div id="content">
      {status === 'active' && shell.may['settings.user_accounts.edit'] && (
        <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setAdding(true)}>
          <i className="fas fa-plus" aria-hidden="true"></i> New user
        </button>
      )}
      <h4>
        <strong>{heading}</strong> user accounts
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        {lead} <strong>accounts</strong>
      </label>
      <hr />

      <div
        onClick={(event) => {
          const target = event.target as HTMLElement
          for (const [cls, kind] of [
            ['.od-remove-user', 'remove'],
            ['.od-block-user', 'block'],
            ['.od-activate-user', 'activate'],
          ] as const) {
            const hit = target.closest(cls)
            if (hit) {
              act(kind, Number(hit.getAttribute('data-user') ?? 0))
              return
            }
          }
        }}
      >
        <DataTable
          id={`settings-users-${status}`}
          columns={columns}
          rows={rows}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.error ? failed(table.error) : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => `row_${Number(row.ID)}`}
          emptyHint="No accounts here."
          details={(row) => <AccountWorkspace userId={Number(row.ID)} />}
        />
      </div>

      <NewUserModal
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={() => {
          setAdding(false)
          reload()
        }}
      />
    </div>
  )
}

/* =======================================================================
 * "New user" (active/addUserModal.php)
 * ==================================================================== */

/**
 * Cont nou: intai ce nu se poate face fara, restul pe file.
 *
 * Filele sunt doar haine - formularul e unul singur si se trimite intreg, deci
 * nimic din ce s-a scris pe o fila inchisa nu se pierde. TOATE campurile
 * obligatorii stau pe fila intai; cand browserul se opreste pe un camp de pe
 * alta, i se deschide fila lui (`index-1.js` facea la fel, cu ascultatorul de
 * `invalid`).
 */
const NEW_USER_TABS = [
  { id: 'nuAccount', label: 'Account' },
  { id: 'nuContact', label: 'Contact' },
  { id: 'nuOrg', label: 'Organisation' },
  { id: 'nuWork', label: 'Work' },
]

function NewUserModal({ open, onClose, onSaved }: { open: boolean; onClose(): void; onSaved(): void }) {
  const form = useQuery({ ...newAccountFormQuery, enabled: open })
  const [tab, setTab] = useState('nuAccount')
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [photo, setPhoto] = useState<File | null>(null)

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    if (photo) data.set('photo', photo)
    setBusy(true)
    setErrors([])
    void createAccount(data)
      .then((answer) => {
        toast(
          answer.mailed ? 'User created. The account details were emailed.' : 'User created, but the email did not go out.',
          'Success',
        )
        setPhoto(null)
        setTab('nuAccount')
        onSaved()
      })
      .catch((error: unknown) => {
        const messages = errorMessages(error)
        setErrors(messages.length > 0 ? messages : [failed(error)])
      })
      .finally(() => setBusy(false))
  }

  return (
    <Modal id="newUserModal" open={open} onClose={onClose} title="New user" labelledBy="newUserModalLabel" large>
      <form
        className="form-horizontal"
        method="post"
        id="newUserForm"
        onSubmit={submit}
        /* Campul gresit isi deschide fila: browserul refuza sa se duca la un
           camp pe care nu-l poate arata, si omul apasa Save fara sa se intample
           nimic. `invalid` nu bulboneaza, deci se asculta in faza de coborare. */
        onInvalidCapture={(event) => {
          const pane = (event.target as HTMLElement).closest('[data-pane]')
          const id = pane?.getAttribute('data-pane')
          if (id && id !== tab) setTab(id)
        }}
      >
        {errors.length > 0 ? (
          <div className="form-errors">
            {errors.map((message) => (
              <div className="alert alert-danger" key={message}>
                <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {message}
              </div>
            ))}
          </div>
        ) : null}

        <ul className="nav nav-tabs od-nu-tabs" role="tablist">
          {NEW_USER_TABS.map((item) => (
            <li key={item.id} className={item.id === tab ? 'active' : ''}>
              <a
                href={'#' + item.id}
                onClick={(event) => {
                  event.preventDefault()
                  setTab(item.id)
                }}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="tab-content od-nu-body">
          {/* ------------------------------------------------ Account */}
          <div
            className={tab === 'nuAccount' ? 'tab-pane active' : 'tab-pane'}
            id="nuAccount"
            data-pane="nuAccount"
            style={tab === 'nuAccount' ? undefined : { display: 'none' }}
          >
            <p className="od-nu-lead">
              Everything the account cannot exist without. The other tabs are optional and can be filled in later.
            </p>

            <Row label="Firstname" required>
              <input type="text" className="form-control input-sm" name="firstname" required />
            </Row>
            <Row label="Lastname" required>
              <input type="text" className="form-control input-sm" name="lastname" required />
            </Row>
            <Row label="Email" required>
              <input type="email" className="form-control input-sm" name="email" required />
              <span className="od-nu-help">
                This is the login. The account details, and the second-factor code, are sent here.
              </span>
            </Row>
            <Row label="Access" required>
              <select className="form-control input-sm" name="access" required defaultValue="2">
                {offeredAccessLevels(form.data?.accessLevels ?? [], null).map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
              <span className="od-nu-help">
                Which companies the person sees; what they can do comes from the role.
                <AccessLevelLegend what="Access" levels={offeredAccessLevels(form.data?.accessLevels ?? [], null)} />
              </span>
            </Row>
            {/*
              Rolul ramane aici desi serverul nu-l cere: fara el omul intra in
              portal fara drepturi si se intoarce a doua zi sa intrebe de ce nu
              vede nimic.
            */}
            <Row label="Role" required>
              <select className="form-control input-sm" name="role" required defaultValue="">
                <option value="">Select...</option>
                {(form.data?.roles ?? []).map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              <span className="od-nu-help">What the person may do once inside.</span>
            </Row>

            <hr />

            <Row label="Password" required>
              <input type="password" className="form-control input-sm" name="password" required />
              <span className="od-nu-help">{form.data?.passwordHint ?? ''}</span>
            </Row>
            <Row label="Confirm Password" required>
              <input type="password" className="form-control input-sm" name="password_confirm" required />
            </Row>
          </div>

          {/* ------------------------------------------------ Contact */}
          <div
            className={tab === 'nuContact' ? 'tab-pane active' : 'tab-pane'}
            id="nuContact"
            data-pane="nuContact"
            style={tab === 'nuContact' ? undefined : { display: 'none' }}
          >
            <p className="od-nu-lead">How the person is reached. All optional.</p>
            <Row label="Phone">
              <input type="text" className="form-control input-sm" name="phone" />
            </Row>
            <Row label="Mobile Phone">
              <input type="text" className="form-control input-sm" name="mobile_phone" />
            </Row>
            <Row label="Country">
              <select className="form-control input-sm" name="country" defaultValue="">
                <option value="">Select...</option>
                {(form.data?.countries ?? []).map((country) => (
                  <option key={country.id} value={country.id}>
                    ({country.prefix}) {country.name}
                  </option>
                ))}
              </select>
            </Row>
            <Row label="Photo">
              <input
                type="file"
                className="form-control input-sm"
                accept="image/png,image/jpeg,image/gif"
                onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
              />
            </Row>
          </div>

          {/* ------------------------------------------- Organisation */}
          <div
            className={tab === 'nuOrg' ? 'tab-pane active' : 'tab-pane'}
            id="nuOrg"
            data-pane="nuOrg"
            style={tab === 'nuOrg' ? undefined : { display: 'none' }}
          >
            <p className="od-nu-lead">Where the person sits in the company. All optional.</p>
            <Row label="Group">
              <select className="form-control input-sm" name="department" defaultValue="">
                <option value="">Select...</option>
                {(form.data?.departments ?? []).map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </Row>
            <Row label="Position">
              <input type="text" className="form-control input-sm" name="position" />
            </Row>
            <Row label="Internal Rank">
              <input type="text" className="form-control input-sm" name="internal_rank" />
            </Row>
            <Row label="IT Manager">
              <input type="checkbox" className="create-user-icheck mt-1" name="it_manager" value="1" />
            </Row>
          </div>

          {/* --------------------------------------------------- Work */}
          <div
            className={tab === 'nuWork' ? 'tab-pane active' : 'tab-pane'}
            id="nuWork"
            data-pane="nuWork"
            style={tab === 'nuWork' ? undefined : { display: 'none' }}
          >
            <p className="od-nu-lead">How the person works. All optional.</p>
            <Row label="Onsite">
              <select className="form-control input-sm" name="onsite" defaultValue="0">
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </Row>
            <Row label="Car">
              <select className="form-control input-sm" name="car" defaultValue="0">
                <option value="0">No</option>
                <option value="1">Personal</option>
                <option value="2">Company</option>
              </select>
            </Row>
            <Row label="GPS tracking">
              <input type="checkbox" className="create-user-icheck mt-1" name="gps_tracking" value="1" />
            </Row>
            <Row label="Signature">
              <select className="form-control input-sm" name="signature_template" defaultValue="">
                <option value="">Select...</option>
                {(form.data?.signatures ?? []).map((signature) => (
                  <option key={signature.id} value={signature.id}>
                    {signature.name}
                  </option>
                ))}
              </select>
            </Row>
            <Row label="Enable quick chat support">
              <input type="checkbox" name="has_quick_chat_support" value="on" id="has_quick_chat_support_new" />
            </Row>
          </div>
        </div>

        <div className="form-group od-nu-actions">
          <div className="col-sm-offset-4 col-sm-8 aright">
            <button type="button" className="btn btn-default" data-dismiss="modal" onClick={onClose}>
              Close
            </button>{' '}
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

function Row({
  label,
  required = false,
  children,
}: {
  label: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <div className="form-group">
      <label className="col-sm-4 control-label">
        {label} {required ? <span className="required">*</span> : null}
      </label>
      <div className="col-sm-8">{children}</div>
    </div>
  )
}
